import React, { useEffect, useState, useRef } from "react";
import httpClient from "../../lib/HttpClient";
import closeBlack from "../../images/icons/svg/close-black.svg";
import history from "../../history";

import { PDFViewer as PaperviewPDF } from "@paperviewpdf/react-viewer";
import security from "../../services/Security";
import { loadDocumentUrl } from "../../lib/axios-client";
import { waitForPdfReady } from "../../lib/pdfReadiness";
import VideoApp from "../Video/VideoApp";
import Spinner from "../Common/Spinner";
import { Col } from "react-grid-system";
import Lottie from "react-lottie";
import loadingAnimation from "../../images/lotties/loading.json";
import styles from "./DashboardPreviewModal.module.scss";

export default function DashboardPreviewModal(props) {
  var showDashboardPreviewModal = props.showDashboardPreviewModal;
  var setShowDashboardPreviewModal = props.setShowDashboardPreviewModal;
  var previewDoc = props.previewDoc;
  var conversationPreview = props.conversationPreview;
  var setConversationPreview = props.setConversationPreview;
  var documentsInReview = props.documentsInReview;
  var setDocumentsInReview = props.setDocumentsInReview;
  var selectedComment = props.selectedComment;
  var setSelectedComment = props.setSelectedComment;

  var registryRef = useRef(null);
  var xfdfPluginRef = useRef(null);
  var scrollPluginRef = useRef(null);

  var [video, setVideo] = useState(null);
  var [showLoader, setShowLoader] = useState(true);
  var [documentUrl, setDocumentUrl] = useState(null);
  var [pdfReady, setPdfReady] = useState(false);
  var [pdfTimedOut, setPdfTimedOut] = useState(false);

  var documentContainer = null;

  useEffect(
    function () {
      var cancelPdfReadyWait = null;

      if (previewDoc.video_id) {
        // Load video instead of PDF
        httpClient
          .get("/documents/" + previewDoc.id + "/preview_video.json")
          .then(function (res) {
            setShowLoader(false);
            setVideo(res.data.video_document);
            if (!conversationPreview) {
              clearDocumentNotification();
            }
          });
      } else {
        // Build document URL for PDF
        var token = security.getToken();
        var url =
          loadDocumentUrl() +
          "/documents/" +
          previewDoc.id +
          "/file.pdf" +
          "?token=" +
          token;
        setDocumentUrl(url);

        // The PDF rendition may still be generating (office uploads); the
        // viewer is not initialized until it is ready.
        setPdfReady(false);
        setPdfTimedOut(false);
        cancelPdfReadyWait = waitForPdfReady({
          documentId: previewDoc.id,
          document: previewDoc,
          fileUrl: url,
          onReady: function () {
            setPdfReady(true);
          },
          onTimeout: function () {
            setShowLoader(false);
            setPdfTimedOut(true);
          },
        });
      }

      return function () {
        if (cancelPdfReadyWait) {
          cancelPdfReadyWait();
        }
      };
    },
    [previewDoc.id]
  );

  function clearDocumentNotification() {
    var documentsInReviewCopy = documentsInReview.slice();
    var previewDocCopy = Object.assign({}, previewDoc);
    previewDocCopy.document_has_notifications = false;
    var index = documentsInReview.indexOf(previewDoc);
    documentsInReviewCopy.splice(index, 1, previewDocCopy);
    setDocumentsInReview(documentsInReviewCopy);
  }

  function handleViewerReady(registry) {
    registryRef.current = registry;

    var xfdfProvider = registry.getPlugin("xfdf");
    if (xfdfProvider) {
      xfdfPluginRef.current = xfdfProvider.provides();
    }

    var scrollProvider = registry.getPlugin("scroll");
    if (scrollProvider) {
      scrollPluginRef.current = scrollProvider.provides();
    }

    // TODO: Replace both setTimeouts (here and in loadCommentAnnotations) with
    // proper PaperView PDF events. Currently:
    //   1. 500ms delay before importing annotations — without this, page
    //      dimensions aren't ready and annotations render in wrong positions.
    //   2. 500ms delay before scrollToPage — without this, the scroll position
    //      resets after the annotation import triggers a re-render.
    // Tried: onDocumentOpened (too early), onLayoutReady (blank pages).
    // Need a reliable "pages fully rendered" event from PaperView PDF.
    setTimeout(function () {
      loadCommentAnnotations();
    }, 500);
  }

  function loadCommentAnnotations() {
    setShowLoader(false);

    if (conversationPreview && conversationPreview.xfdf_highlight_id) {
      // Import the annotation XFDF
      if (xfdfPluginRef.current && conversationPreview.xfdf_highlight_string) {
        try {
          xfdfPluginRef.current.importXfdf(
            conversationPreview.xfdf_highlight_string
          );
        } catch (err) {
          // Annotation import failed - viewer still usable
        }
      }

      // Navigate to the page where the annotation is.
      // Deferred — scroll must happen after the viewer finishes processing
      // the imported annotation, otherwise the position resets.
      if (scrollPluginRef.current && conversationPreview.page) {
        setTimeout(function () {
          scrollPluginRef.current.scrollToPage({
            pageNumber: conversationPreview.page.page_number,
            behavior: "instant",
          });
        }, 500);
      }
    } else {
      clearDocumentNotification();
    }
  }

  function hideModal() {
    setShowDashboardPreviewModal(false);
    setConversationPreview(null);
    setSelectedComment(null);
  }

  function openFile() {
    var commentUrl;

    if (selectedComment) {
      if (selectedComment.conversation.xfdf_highlight_id) {
        commentUrl =
          "/drafts/" +
          previewDoc.id +
          "?xfdfId=" +
          selectedComment.conversation.xfdf_highlight_id;
      } else {
        commentUrl =
          "/drafts/" + previewDoc.id + "?xfdfId=" + previewDoc.id + "-general";
      }
    } else {
      commentUrl = "/drafts/" + previewDoc.id;
    }

    setSelectedComment(null);
    history.push(commentUrl);
  }

  var defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimation,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <div id="document_container" className={styles.modalContainer}>
      <div
        id="document_view_header"
        className={styles.activityLogHeaderContainer}
      >
        <h1 className={styles.activityLogHeading}>{previewDoc.title}</h1>
        <div
          className={video ? styles.rightContainerVideo : styles.rightContainer}
        >
          <a
            className={video ? styles.actionButtonVideo : styles.actionButton}
            onClick={openFile}
          >
            Open file
          </a>
          <img
            className={styles.closeButton}
            onClick={hideModal}
            src={closeBlack}
          />
        </div>
      </div>
      {video && (
        <div
          className={
            video ? styles.videoPreviewContainer : styles.webViewerContainer
          }
        >
          {showLoader && (
            <div className={styles.loaderContainer}>
              <Lottie options={defaultOptions} height={92} width={82} />
            </div>
          )}
          <React.Fragment>
            {video && (
              <React.Fragment>
                <Col
                  sm={9}
                  ref={function (element) {
                    documentContainer = element;
                  }}
                >
                  <VideoApp doc={video} documentContainer={documentContainer} />
                </Col>
              </React.Fragment>
            )}
          </React.Fragment>
        </div>
      )}
      {!video && documentUrl && !pdfReady && (
        <div className={styles.webViewerContainer}>
          <div className={styles.processingContainer}>
            {pdfTimedOut ? (
              <div>
                Your document could not be processed. Please refresh the page to
                try again.
              </div>
            ) : (
              <Spinner text="Processing document..." />
            )}
          </div>
        </div>
      )}
      {!video && documentUrl && pdfReady && (
        <div className={styles.webViewerContainer}>
          {showLoader && (
            <div className={styles.loaderContainer}>
              <Lottie options={defaultOptions} height={92} width={82} />
            </div>
          )}
          <PaperviewPDF
            style={{ width: "100%", height: "100%" }}
            onReady={handleViewerReady}
            config={{
              src: documentUrl,
              wasmUrl: "/pdfium.wasm",
              worker: true,
              theme: {
                preference: "light",
              },
              disabledCategories: [
                "annotation",
                "redaction",
                "selection",
                "history",
                "tools",
                "mode-annotate",
                "mode-shapes",
                "mode-redact",
                "document",
                "panel-comment",
              ],
            }}
          />
        </div>
      )}
    </div>
  );
}
